/**
 * Tela /campaigns/templates — Galeria de templates de campanha.
 *
 * Lista todos os templates disponíveis em grade visual com filtros por
 * categoria (`classica` | `promocional`) e busca textual. Filtros
 * persistem na query string via TanStack Router (validateSearch definido
 * em router.tsx).
 *
 * Fluxo clone-and-customize (TH-55110):
 *   1. Usuário clica "Usar este template" no card.
 *   2. Modal abre pedindo nome da campanha (default = nome do template).
 *   3. Submit chama `POST /api/campaigns/from-template/{slug}` com
 *      `{name}` no body.
 *   4. Em sucesso, navega para `/campaigns/{id}` (editor de campanha).
 *
 * Fallback mock: enquanto o backend retorna 404, o hook degrada para
 * mocks; o fluxo de UI continua exercitável em dev.
 *
 * TH-55110 — clone-and-customize com modal de nome (estende TH-55142).
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { ArrowLeft, Search } from "lucide-react";

import { AppLayout } from "@/components/AppLayout";
import { CampaignTemplateCard } from "@/components/CampaignTemplateCard";
import { CloneCampaignTemplateDialog } from "@/components/CloneCampaignTemplateDialog";
import { RequireAuth } from "@/components/RequireAuth";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Select } from "@/components/ui/Select";
import { Skeleton } from "@/components/ui/Skeleton";
import {
  useCampaignTemplates,
  useCloneCampaignTemplate,
  type CampaignTemplateRead,
} from "@/hooks/useCampaignTemplates";

// ---------------------------------------------------------------------------
// Tipos de search params (espelhados em router.tsx validateSearch)
// ---------------------------------------------------------------------------

export interface CampaignTemplatesSearch {
  category?: string;
  q?: string;
}

// ---------------------------------------------------------------------------
// Opções de categoria para o Select — inclui todas + dinâmico a partir dos
// templates disponíveis, mas por simplicidade usamos o conjunto fixo que
// espelha os mocks e o seed planejado.
// ---------------------------------------------------------------------------

// Backend `GET /api/campaign-templates?category=` valida o pattern
// `^(classica|promocional)$`. Outras categorias (área de departamento)
// poderiam virar segundo eixo de filtro no futuro — ainda não estão no
// modelo `campaign_templates`.
const CATEGORY_OPTIONS: { value: string; label: string }[] = [
  { value: "", label: "Todas as categorias" },
  { value: "classica", label: "Clássicas" },
  { value: "promocional", label: "Promocionais" },
];

// ---------------------------------------------------------------------------
// Hook: debounce de 300 ms para o campo de busca
// ---------------------------------------------------------------------------

function useDebouncedValue(value: string, delayMs: number): string {
  const [debounced, setDebounced] = useState(value);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setDebounced(value), delayMs);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [value, delayMs]);

  return debounced;
}

// ---------------------------------------------------------------------------
// Filtragem client-side sobre o mock (remove quando backend filtrar)
// ---------------------------------------------------------------------------

function applyClientFilters(
  templates: CampaignTemplateRead[],
  category: string,
  search: string,
): CampaignTemplateRead[] {
  let result = templates;
  if (category) {
    result = result.filter((t) => t.category === category);
  }
  if (search.trim()) {
    const lower = search.toLowerCase();
    result = result.filter(
      (t) =>
        t.name.toLowerCase().includes(lower) ||
        (t.description?.toLowerCase().includes(lower) ?? false),
    );
  }
  return result;
}

// ---------------------------------------------------------------------------
// Skeleton de loading
// ---------------------------------------------------------------------------

function GallerySkeleton() {
  return (
    <div
      className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
      aria-label="Carregando templates…"
    >
      {Array.from({ length: 8 }).map((_, i) => (
        <div
          key={i}
          className="overflow-hidden rounded-lg border border-[var(--color-border)] bg-[var(--color-card)]"
        >
          <Skeleton className="aspect-video w-full rounded-none" />
          <div className="flex flex-col gap-2 p-4">
            <Skeleton className="h-4 w-3/4" />
            <Skeleton className="h-3 w-full" />
            <Skeleton className="h-3 w-2/3" />
            <Skeleton className="mt-2 h-8 w-full" />
          </div>
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Página
// ---------------------------------------------------------------------------

export function CampaignTemplatesGalleryPage() {
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as CampaignTemplatesSearch;

  // Filtros locais sincronizados com a query string
  const [categoryFilter, setCategoryFilter] = useState<string>(
    search.category ?? "",
  );
  const [searchInput, setSearchInput] = useState<string>(search.q ?? "");
  const debouncedSearch = useDebouncedValue(searchInput, 300);

  // Persistir filtros na URL quando o debounce settle (não bloqueante)
  useEffect(() => {
    void navigate({
      to: "/campaigns/templates",
      search: {
        ...(categoryFilter ? { category: categoryFilter } : {}),
        ...(debouncedSearch.trim() ? { q: debouncedSearch.trim() } : {}),
      },
      replace: true,
    });

  }, [categoryFilter, debouncedSearch]);

  function handleCategoryChange(next: string) {
    setCategoryFilter(next);
  }

  function handleSearchChange(next: string) {
    setSearchInput(next);
  }

  // Query (passa filtros pra quando o backend existir)
  const templatesQuery = useCampaignTemplates({
    category: categoryFilter || undefined,
    search: debouncedSearch || undefined,
  });

  // Clone-and-customize: clique no card abre modal de nome.
  // Submit do modal dispara a mutation; sucesso navega para o editor.
  const cloneMutation = useCloneCampaignTemplate();
  const [selectedTemplate, setSelectedTemplate] =
    useState<CampaignTemplateRead | null>(null);
  const [cloneError, setCloneError] = useState<string | null>(null);

  function handleUse(template: CampaignTemplateRead) {
    if (cloneMutation.isPending) return;
    setCloneError(null);
    setSelectedTemplate(template);
  }

  function handleCloseDialog() {
    if (cloneMutation.isPending) return;
    setSelectedTemplate(null);
  }

  async function handleConfirmClone(payload: { name: string }) {
    if (!selectedTemplate) return;
    setCloneError(null);
    try {
      const result = await cloneMutation.mutateAsync({
        slug: selectedTemplate.slug,
        name: payload.name,
      });
      setSelectedTemplate(null);
      void navigate({
        to: "/campaigns/$campaignId",
        params: { campaignId: result.id },
      });
    } catch (err) {
      setCloneError(
        err instanceof Error ? err.message : "Falha ao criar campanha.",
      );
    }
  }

  // Filtragem client-side para o mock (quando backend retornar os dados já filtrados isso é no-op)
  const rawTemplates = templatesQuery.data?.data ?? [];
  const templates = useMemo(
    () => applyClientFilters(rawTemplates, categoryFilter, debouncedSearch),
    [rawTemplates, categoryFilter, debouncedSearch],
  );

  const anyCloning = cloneMutation.isPending;

  return (
    <RequireAuth>
      <AppLayout
        breadcrumb="OPERAÇÃO / CAMPANHAS / TEMPLATES"
        title="Galeria de templates"
        actions={
          <Link
            to="/campaigns"
            className="inline-flex h-8 items-center justify-center gap-2 rounded-md border border-[var(--color-border-strong)] bg-transparent px-3 text-xs font-medium transition-colors hover:bg-[var(--color-secondary)]"
          >
            <ArrowLeft className="h-4 w-4" aria-hidden="true" />
            Voltar
          </Link>
        }
      >
        <div className="mx-auto max-w-7xl space-y-6">
          {/* Descrição */}
          <p className="text-sm text-[var(--color-muted-foreground)]">
            Escolha um template pronto para começar sua campanha mais rápido.
            Ele será clonado como rascunho e você ajusta os detalhes depois.
          </p>

          {/* Toolbar */}
          <div
            className="flex flex-col gap-3 sm:flex-row sm:items-center"
            role="search"
            aria-label="Filtros de templates"
          >
            {/* Busca textual */}
            <div className="relative flex-1">
              <Search
                className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--color-muted-foreground)]"
                aria-hidden="true"
              />
              <Input
                type="search"
                placeholder="Buscar templates…"
                aria-label="Buscar templates por nome ou descrição"
                value={searchInput}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="pl-9"
              />
            </div>

            {/* Filtro de categoria */}
            <label className="sr-only" htmlFor="category-filter">
              Filtrar por categoria
            </label>
            <Select
              id="category-filter"
              aria-label="Filtrar por categoria"
              value={categoryFilter}
              onChange={(e) => handleCategoryChange(e.target.value)}
              className="sm:w-56"
              data-testid="category-filter"
            >
              {CATEGORY_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </Select>
          </div>

          {/* Estados: loading */}
          {templatesQuery.isLoading && <GallerySkeleton />}

          {/* Estado: erro */}
          {templatesQuery.isError && (
            <div
              role="alert"
              className="rounded-md border border-[var(--color-destructive)] bg-[var(--color-destructive)]/10 px-4 py-3 text-sm text-[var(--color-destructive)]"
              data-testid="gallery-error"
            >
              <p className="font-medium">Erro ao carregar templates</p>
              <p className="mt-1 text-xs">{templatesQuery.error.message}</p>
              <Button
                variant="outline"
                size="sm"
                className="mt-3"
                onClick={() => void templatesQuery.refetch()}
              >
                Tentar novamente
              </Button>
            </div>
          )}

          {/* Estado: empty */}
          {!templatesQuery.isLoading &&
            !templatesQuery.isError &&
            templates.length === 0 && (
              <div
                className="flex flex-col items-center gap-3 py-16 text-center"
                data-testid="gallery-empty"
              >
                <p className="text-sm font-medium text-[var(--color-foreground)]">
                  Nenhum template encontrado
                </p>
                <p className="text-xs text-[var(--color-muted-foreground)]">
                  {categoryFilter || debouncedSearch
                    ? "Tente ajustar os filtros."
                    : "Ainda não há templates disponíveis."}
                </p>
                {(categoryFilter || debouncedSearch) && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setCategoryFilter("");
                      setSearchInput("");
                    }}
                  >
                    Limpar filtros
                  </Button>
                )}
              </div>
            )}

          {/* Modal de confirmação — pede nome da campanha antes do clone. */}
          <CloneCampaignTemplateDialog
            template={selectedTemplate}
            isCloning={cloneMutation.isPending}
            errorMessage={cloneError}
            onClose={handleCloseDialog}
            onConfirm={(payload) => void handleConfirmClone(payload)}
          />

          {/* Grid */}
          {!templatesQuery.isLoading && templates.length > 0 && (
            <>
              {cloneError && (
                <div
                  role="alert"
                  className="rounded-md border border-[var(--color-destructive)] bg-[var(--color-destructive)]/10 px-4 py-2 text-sm text-[var(--color-destructive)]"
                  data-testid="clone-error"
                >
                  {cloneError}
                </div>
              )}

              <ul
                className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
                data-testid="template-gallery-grid"
              >
                {templates.map((tpl) => (
                  <li key={tpl.id}>
                    <CampaignTemplateCard
                      template={tpl}
                      isCloning={
                        anyCloning && selectedTemplate?.id === tpl.id
                      }
                      anyCloning={anyCloning}
                      onUse={handleUse}
                    />
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      </AppLayout>
    </RequireAuth>
  );
}
